import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Typography, Button, IconButton, Tooltip } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import moment from "moment";
import CurrencyFormat from "react-currency-format";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { defaultVariables } from "../../../config";
import { tableIcons } from "../../../config/design/TableIcons";
import DepositMoneyDialog from "./DepositMoneyDialog";
import MyWallet from "../../components/elements/MyWallet";
import AppCardLoaderFields from "../../components/loaders/AppCardLoaderFields";
import {getDataTableLocalization} from "../../../config/Translate";
import {currencySymbol, showTableDate, truncate} from "../../../config/values";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import WithdrawMoneyDialog from "./WithdrawMoneyDialog";
import {useNavigate} from "react-router-dom";

const Wallet = (props) => {
    const tableRef = useRef();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [columns] = useState([
        { title: t('uniqueCode'), field: 'unique_code', filterPlaceholder: t('search') },

        {
            field: "type",
            title: t("transactionType"),
            filtering: false,
            lookup: {
                deposit: (
                    <Typography style={{ color: "#38bf84" }}>
                        {t("deposit")}
                    </Typography>
                ),
                withdraw: (
                    <Typography style={{ color: "#BF8230" }}>
                        {t("withdraw")}
                    </Typography>
                ),
            },
        },
        {
            title: t("amount"),
            field: "amount",
            filterPlaceholder: t("search"),
            render: (rowData) => (
                <CurrencyFormat
                    value={rowData.amount}
                    displayType={"text"}
                    thousandSeparator={true}
                    suffix={" " + currencySymbol}
                />
            ),
        },
        {
            title: t("transactionTrackingCode"),
            field: "track_code",
            filterPlaceholder: t("search"),
        },
        {
            title: t('description'),
            field: 'description' ,
            render: rowData => <Tooltip arrow title={rowData.description}>{truncate(rowData.description,20)}</Tooltip>
        },
        {
            title: t("status"),
            field: "status",
            lookup: {
                confirmed: <Typography className="table-badge active">{t('confirmed')}</Typography>,
                pending: <Typography className="table-badge pending">{t('pending')}</Typography>,
                not_paid: <Typography className="table-badge inactive">{t("not_paid")}</Typography>,
            },
        },
        {
            title: t("created_at"),
            field: "created_at",
            render: (rowData) => (
                <Typography>
                    {showTableDate(rowData.created_at)}
                </Typography>
            ),
        },
    ]);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(() => {
        if (tableRef.current) {
            tableRef.current.onQueryChange();
        }
    }, []);

    const handleRemoveItem = async (id) => {
        const loginToken = localStorage.getItem("ACT");
        try {
            const config = {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${loginToken}`,
                },
            };
            const data = new FormData();
            data.append("id", id);

            await axios.post(`${defaultVariables.url}/wallet/removeHistory`, data, config);
            enqueueSnackbar(t("dataRemoved"), { variant: "success" });
            loadData();
        } catch (error) {
            enqueueSnackbar(t("dataRemovingFailed"), { variant: "error" });
        }
    };

    return (
        <div className="main-table">
            {loading ? (
                <AppCardLoaderFields />
            ) : (
                <MaterialTable
                    tableRef={tableRef}
                    icons={tableIcons}
                    title={
                        <>
                            <DepositMoneyDialog style={{ marginRight: 10 }} reload={loadData} />
                            <WithdrawMoneyDialog style={{ marginRight: 10 }} reload={loadData} />
                            <MyWallet />
                        </>
                    }
                    localization={getDataTableLocalization()}
                    columns={columns}
                    data={(query) =>
                        new Promise((resolve) => {
                            const loginToken = localStorage.getItem("ACT");
                            const config = {
                                headers: {
                                    Accept: "application/json",
                                    Authorization: `Bearer ${loginToken}`,
                                },
                            };
                            axios
                                .get(
                                    `${defaultVariables.url}/wallets/myHistory?page=${
                                        query.page + 1
                                    }&per_page=${query.pageSize}&search=${query.search}`,
                                    config
                                )
                                .then((response) => {
                                    const data = response.data.data.map((row) => ({
                                        amount: row.amount,
                                        id: row.id,
                                        description: row.description,
                                        status: row.status,
                                        type: row.type,
                                        track_code: row.track_code,
                                        unique_code: row.unique_code,
                                        created_at: row.created_at,
                                    }));
                                    resolve({
                                        data: data,
                                        page: response.data.current_page - 1,
                                        totalCount: response.data.total,
                                    });
                                });
                        })
                    }
                    options={{
                        pageSize: 10,
                        pageSizeOptions: [5, 10, 20],
                        filtering: false,
                        search: false,
                    }}
                />
            )}
        </div>
    );
};

export default Wallet;
